import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PopuliBotDemoPage.css";

const API = {
  accessList: "https://populi-document-listining-7kpxqstxwq-ey.a.run.app",
  bot: "https://populi-botdemo-795950092325.europe-west3.run.app",
};

function Icon({ name }) {
  const paths = {
    send: <path d="m3 3 18 9-18 9 4-9-4-9Zm4 9h14" />,
    reset: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    volume: <path d="M5 10v4h4l5 4V6l-5 4H5Zm12-1a4 4 0 0 1 0 6m2-9a8 8 0 0 1 0 12" />,
  };
  return <svg className="bot-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function appendFormData(values) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => formData.append(key, value));
  return formData;
}

async function postForm(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) {
    throw new Error("Ağ bağlantısına izin verilmedi. VPN, reklam engelleyici veya güvenlik uygulamalarını kontrol edin.");
  }
  return response.json();
}

function createSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export default function PopuliBotDemoPage({ userEmail: emailProp, onUnauthorized }) {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [audioSource, setAudioSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sessionIdRef = useRef(createSessionId());
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);
  const audioRef = useRef(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const selectedAccess = useMemo(
    () => roles.find((item) => item.role === selectedRole) || roles[0] || null,
    [roles, selectedRole],
  );

  const loadAccessList = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.accessList, { useremail: userEmail });
      const nextRoles = Array.isArray(data?.result_roles) ? data.result_roles : [];
      setRoles(nextRoles);
      setSelectedRole((current) => (
        nextRoles.some((item) => item.role === current) ? current : nextRoles[0]?.role || ""
      ));
    } catch (requestError) {
      setError(requestError.message || "Erişim listesi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadAccessList();
  }, [loadAccessList, onUnauthorized, userEmail]);

  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }, [answer]);

  useEffect(() => {
    if (!audioSource || !audioRef.current) return;
    audioRef.current.play().catch(() => {
      // Tarayıcı otomatik oynatmayı engellerse kullanıcı ses oynatıcısını kullanabilir.
    });
  }, [audioSource]);

  async function submitAnswer(event) {
    event?.preventDefault();
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer || loading || !selectedAccess) return;

    const nextQuestions = [
      ...questions,
      {
        ar_questionId: questions.length + 1,
        ar_question: currentQuestion,
        ar_answers: trimmedAnswer,
      },
    ];

    setMessages((current) => [...current, { id: `${Date.now()}-user`, type: "user", text: trimmedAnswer }]);
    setQuestions(nextQuestions);
    setAnswer("");
    setLoading(true);
    setError("");

    try {
      const data = await postForm(API.bot, {
        lang: "tr",
        question_list: JSON.stringify(nextQuestions),
        sessionID: sessionIdRef.current,
        dataCenter: selectedAccess.role,
        botname: selectedAccess.botname || "",
      });
      const responseText = data?.text || "Yanıt alınamadı.";
      setCurrentQuestion(responseText);
      setMessages((current) => [...current, { id: `${Date.now()}-bot`, type: "bot", text: responseText }]);
      setAudioSource(data?.audio ? `data:audio/mp3;base64,${data.audio}` : "");
    } catch (requestError) {
      setError(requestError.message || "Yanıt alınamadı. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  }

  function resetConversation() {
    setMessages([]);
    setQuestions([]);
    setCurrentQuestion("");
    setAnswer("");
    setAudioSource("");
    setError("");
    sessionIdRef.current = createSessionId();
  }

  function changeAccess(event) {
    setSelectedRole(event.target.value);
    resetConversation();
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitAnswer();
    }
  }

  if (!userEmail) {
    return <div className="bot-page bot-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="bot-page">
      <section className="bot-shell">
        <div className="bot-access">
          <label>
            <span>Erişim</span>
            <select value={selectedRole} onChange={changeAccess} disabled={loading || !roles.length}>
              {!roles.length && <option value="">Erişim seçeneği bulunamadı</option>}
              {roles.map((item) => <option key={`${item.role}-${item.botname || "bot"}`} value={item.role}>{item.role}</option>)}
            </select>
          </label>
          <button className="bot-reset" type="button" onClick={resetConversation} disabled={!messages.length && !answer}>
            <Icon name="reset" /> Yeni Sohbet
          </button>
        </div>

        <section className="bot-chat" aria-label="Populi demo sohbeti">
          <div className="bot-chat__heading">
            <div className="bot-avatar">P</div>
            <div><h1>Populi Demo</h1><p>{selectedRole || "Erişim seçiliyor…"}</p></div>
          </div>

          <div className="bot-messages" ref={messagesRef} aria-live="polite">
            {!messages.length && (
              <div className="bot-welcome">
                <div className="bot-avatar bot-avatar--large">P</div>
                <h2>Size nasıl yardımcı olabilirim?</h2>
                <p>Sorunuzu aşağıdaki alana yazabilirsiniz.</p>
              </div>
            )}
            {messages.map((message) => (
              <div className={`bot-message bot-message--${message.type}`} key={message.id}>
                {message.type === "bot" && <span className="bot-message__avatar">P</span>}
                <div>{message.text}</div>
              </div>
            ))}
            {loading && (
              <div className="bot-message bot-message--bot">
                <span className="bot-message__avatar">P</span>
                <div className="bot-typing" aria-label="Populi yazıyor"><i /><i /><i /></div>
              </div>
            )}
          </div>

          {error && <div className="bot-alert" role="alert">{error}</div>}

          {audioSource && (
            <div className="bot-audio">
              <Icon name="volume" />
              <audio ref={audioRef} src={audioSource} controls />
            </div>
          )}

          <form className="bot-composer" onSubmit={submitAnswer}>
            <textarea
              ref={textareaRef}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Populi’ye sor!"
              rows="1"
              disabled={loading || !selectedAccess}
              aria-label="Mesajınız"
            />
            <button type="submit" disabled={!answer.trim() || loading || !selectedAccess} aria-label="Gönder">
              <Icon name="send" />
            </button>
          </form>
          <p className="bot-hint">Göndermek için Enter, yeni satır için Shift + Enter</p>
        </section>
      </section>
    </main>
  );
}
